import { useState, type ReactNode } from 'react';
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  FilmSlateIcon,
  GearSixIcon,
} from '@phosphor-icons/react';
import { T } from 'gt-react';
import type { ChatSection } from '../operatorActivityToChat';

export interface OperatorScreenGroupProps {
  /** The build phase this group covers — drives the header label + icon. */
  section: ChatSection;
  /** Edits + renders in this group, shown in the collapsed summary. */
  editCount: number;
  renderCount: number;
  /** The active (last) section starts expanded; completed ones start collapsed. */
  defaultExpanded: boolean;
  children: ReactNode;
}

/** One build phase's turns under a collapsible header — "Setup", "Screen 2 / 5 · 6 edits · 2
 * renders", or "Finalize". Completed sections collapse to the summary so the thread doesn't become
 * a wall; the active section stays expanded. */
export function OperatorScreenGroup({
  section,
  editCount,
  renderCount,
  defaultExpanded,
  children,
}: OperatorScreenGroupProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Icon =
    section.phase === 'setup'
      ? GearSixIcon
      : section.phase === 'finalize'
        ? CheckCircleIcon
        : FilmSlateIcon;

  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted/50"
      >
        <Icon
          className="h-4 w-4 shrink-0 text-muted-foreground"
          weight="bold"
        />
        <span>
          {section.phase === 'setup' ? (
            <T context="collapsible header for the environment-setup phase of a mock UI build">
              Setup
            </T>
          ) : section.phase === 'finalize' ? (
            <T context="collapsible header for the finalize phase of a mock UI build">
              Finalize
            </T>
          ) : (
            <>
              <T context="collapsible header for one storyboard screen in the mock UI build thread">
                Screen
              </T>{' '}
              {section.index + 1}
              {section.total > 0 ? ` / ${section.total}` : ''}
            </>
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-muted-foreground">
          {[
            editCount > 0 ? `${editCount} edits` : null,
            renderCount > 0 ? `${renderCount} renders` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {expanded ? (
          <CaretUpIcon className="h-3 w-3 shrink-0" weight="bold" />
        ) : (
          <CaretDownIcon className="h-3 w-3 shrink-0" weight="bold" />
        )}
      </button>
      {expanded && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          {children}
        </div>
      )}
    </div>
  );
}
